/**
 * api-config.js
 *
 * Central routing configuration for API requests.
 *
 * Modes:
 *
 *   dev  (npm start, no env flag)
 *     → Binance and CoinGecko are called directly from the browser.
 *       Both support CORS on their public endpoints so no proxy is needed.
 *       Pre-computed VPS endpoints are unavailable (server not running).
 *       US Stocks tab is unavailable (requires server-side Yahoo Finance).
 *
 *   vps  (REACT_APP_API_MODE=vps)
 *     → All calls route through the Express server (localhost:3001 in dev,
 *       same-origin in production). Pre-computed data and stocks available.
 *       Requires "npm run server" to be running alongside "npm start".
 *
 *   netlify (default non-dev build without vps flag)
 *     → Calls route through Netlify serverless functions.
 *       Pre-computed data and stocks not available.
 */

const IS_VPS = process.env.REACT_APP_API_MODE === 'vps';
const IS_DEV = process.env.NODE_ENV === 'development';

export const API_MODE = IS_VPS ? 'vps' : 'netlify';

/**
 * Base URL for Binance proxy requests.
 */
export function getBinanceProxyBase() {
  if (IS_VPS) {
    return IS_DEV ? 'http://localhost:3001/api/binance' : '/api/binance';
  }
  if (IS_DEV) {
    // Direct Binance API — no proxy needed in dev, Binance supports CORS
    return 'https://api.binance.com';
  }
  return '/.netlify/functions/binance-proxy';
}

/**
 * Base URL for CoinGecko proxy requests.
 */
export function getCoinGeckoProxyBase() {
  if (IS_VPS) {
    return IS_DEV ? 'http://localhost:3001/api/coingecko' : '/api/coingecko';
  }
  if (IS_DEV) {
    // Direct CoinGecko API — no proxy needed in dev, CoinGecko supports CORS
    return 'https://api.coingecko.com/api/v3';
  }
  return '/.netlify/functions/coingecko-proxy';
}

/**
 * Base URL for Coinbase proxy requests.
 *
 * Coinbase public market endpoints support CORS, so in dev mode we call
 * them directly (same as Binance). In VPS mode we route through the
 * Express proxy so the server can add auth headers if needed in future.
 */
export function getCoinbaseProxyBase() {
  if (IS_VPS) {
    return IS_DEV ? 'http://localhost:3001/api/coinbase' : '/api/coinbase';
  }
  // Dev/netlify: call Coinbase public API directly — CORS is supported
  return 'https://api.coinbase.com/api/v3/brokerage/market';
}

/**
 * Base URL for VPS-specific pre-computed endpoints.
 * Returns null in Netlify/dev mode (endpoints don't exist there).
 */
export function getServerApiBase() {
  if (!IS_VPS) return null;
  return IS_DEV ? 'http://localhost:3001' : '';
}
